/**
 * Cosmic-dark design tokens — THE single source of truth for colour, type,
 * radii, elevation, z-index and motion.
 *
 * Everything else derives from this file:
 *   - MUI theme            → src/theme/index.ts        (imports `tokens`)
 *   - Tailwind utilities   → tailwind.config.ts        (imports `tokens`)
 *   - Dial / SVG layer     → src/types/Constants.ts    (imports `tokens`)
 *   - CSS variables (:root)→ src/styles/tokens.generated.css
 *                            (generated by scripts/generate-token-css.ts from `cssVars`)
 *
 * To change a colour, edit it HERE and rerun the generator (it runs automatically
 * on `predev`/`prebuild`). Do not hardcode hex values anywhere else — the ESLint
 * `no-restricted-syntax` guard will flag them. See DESIGN_TOKENS.md.
 */

export const color = {
	// Base surfaces
	void: "#0b0a1f",
	bg: "#0a0918",
	surface: "#15132b",
	surface2: "#1c1936",
	elevated: "#211d3f",
	glass: "rgba(30, 27, 60, 0.55)",
	glassBorder: "rgba(139, 123, 240, 0.28)",

	// Brand — violet scale
	violet50: "#eeedfe",
	violet200: "#cecbf6",
	violet300: "#b5acf2",
	violet: "#8b7bf0",
	violet600: "#6f63d6",
	violet700: "#534ab7",
	violet900: "#2a2350",

	// Brand — gold accent
	gold: "#f2c879",
	gold600: "#c8923d",
	onGold: "#1b1832", // readable text on a gold fill

	// Dial / chart
	halfsum: "#e6526f",
	chartSurface: "#e8e8e8", // the light-gray disc the dial is drawn on (softer than white)
	dialCircle: "#333333",
	dialSigns: "#000000",
	dialLuminary: "#999aa3",
	dialArrowMuted: "#6b7280", // secondary cardinal arrows
	dialTick: "#9aa0b3", // harmonic tick ring
	dialPointer: "#ed1e27", // active (red) pointer arrow

	// Text
	text: "#ece8fb",
	textStrong: "#f6f3ff",
	textMuted: "#a39dc4",
	textFaint: "#8983a5", // ≥4.5:1 on surface (5.05:1) and bg-void/40 (5.23:1) for small text

	// Borders
	border: "rgba(139, 123, 240, 0.22)",
	borderStrong: "rgba(139, 123, 240, 0.45)",

	// Semantic
	success: "#5dcaa5",
	warning: "#f2c879",
	danger: "#e6526f",

	// Neutrals
	white: "#ffffff",
	black: "#000000",
} as const;

export const radius = {
	sm: "8px",
	md: "11px",
	lg: "16px",
	full: "999px",
} as const;

export const shadow = {
	s1: "0 4px 12px rgba(0, 0, 0, 0.4)",
	s2: "0 8px 32px rgba(0, 0, 0, 0.5)",
	glow: "0 8px 22px rgba(127, 119, 221, 0.45)",
	ring: "0 0 0 1px rgba(139, 123, 240, 0.35)",
} as const;

export const font = {
	display: '"Lora", Georgia, "Times New Roman", serif',
	sans: '"Inter", system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
	mono: '"JetBrains Mono", ui-monospace, "Cascadia Mono", "Roboto Mono", "Courier New", monospace',
} as const;

export const z = {
	dial: 1,
	dock: 10,
	appbar: 20,
	overlay: 1200,
	modal: 1300,
} as const;

export const motion = {
	easeOut: "cubic-bezier(0.22, 1, 0.36, 1)",
	easeInOut: "cubic-bezier(0.65, 0, 0.35, 1)",
	durFast: "140ms",
	dur: "240ms",
	durSlow: "420ms",
} as const;

export const layout = {
	maxWidth: "1100px",
} as const;

/**
 * Typography — the single source for type sizes and weights. `size` is the
 * canonical rem scale; `tag` and `micro` are the SAME 0.6875rem step (kept as
 * two names only because chips and sub-labels want different line-height /
 * tracking — see tailwind.config.ts). The MUI theme and Tailwind read
 * `size`/`weight` directly.
 *
 * `role` below is a set of REFERENCE recipes that mirror what the shared panel
 * primitives hand-roll in Tailwind — it is NOT wired up (nothing imports it).
 * Treat it as the documented home for those recipes, not a live source.
 */
export const type = {
	size: {
		tag: "0.6875rem", // level chips (N / D / Db / T) — same step as `micro`
		micro: "0.6875rem", // uppercase sub-labels
		xs: "0.75rem", // captions, orbs, intros
		sm: "0.875rem", // body / rows / controls
		base: "1rem", // panel title
		lg: "1.125rem", // empty / placeholder headings
		xl: "1.25rem", // section title (MUI subheader)
		data: "1.5rem", // big readout number
	},
	weight: {
		regular: 400,
		medium: 500,
		semibold: 600,
	},
	tracking: {
		wide: "0.05em",
	},
	// Role recipes — { family, size, weight, [transform], [tracking] }.
	role: {
		panelTitle: { family: font.display, size: "1rem", weight: 600 },
		sectionLabel: { family: font.mono, size: "0.75rem", weight: 400 },
		subLabel: {
			family: font.sans,
			size: "0.6875rem",
			weight: 600,
			transform: "uppercase",
			tracking: "0.05em",
		},
		tag: { family: font.sans, size: "0.65rem", weight: 600 },
		body: { family: font.sans, size: "0.875rem", weight: 400 },
		caption: { family: font.sans, size: "0.75rem", weight: 400 },
		data: { family: font.mono, size: "0.875rem", weight: 400 },
		dataLg: { family: font.mono, size: "1.5rem", weight: 400 },
	},
} as const;

export const tokens = { color, radius, shadow, font, z, motion, layout, type } as const;

/**
 * CSS custom-property surface. Names are the canonical `--kebab` tokens used by
 * the raw-CSS rules in globals.css; values reference the structured tokens above
 * (no hex duplication). The generator writes these into tokens.generated.css.
 */
export const cssVars: Record<string, string> = {
	"--void": color.void,
	"--bg": color.bg,
	"--surface": color.surface,
	"--surface-2": color.surface2,
	"--elevated": color.elevated,
	"--glass": color.glass,
	"--glass-border": color.glassBorder,

	"--violet-50": color.violet50,
	"--violet-200": color.violet200,
	"--violet-300": color.violet300,
	"--violet": color.violet,
	"--violet-600": color.violet600,
	"--violet-700": color.violet700,
	"--violet-900": color.violet900,

	"--gold": color.gold,
	"--gold-600": color.gold600,

	"--halfsum": color.halfsum,
	"--pointer": color.violet,
	"--dial-arrow": color.gold,

	"--text": color.text,
	"--text-strong": color.textStrong,
	"--text-muted": color.textMuted,
	"--text-faint": color.textFaint,

	"--border": color.border,
	"--border-strong": color.borderStrong,

	"--success": color.success,
	"--warning": color.warning,
	"--danger": color.danger,

	"--radius-sm": radius.sm,
	"--radius-md": radius.md,
	"--radius-lg": radius.lg,
	"--radius-full": radius.full,

	"--shadow-1": shadow.s1,
	"--shadow-2": shadow.s2,
	"--glow-violet": shadow.glow,
	"--ring-violet": shadow.ring,

	"--font-display": font.display,
	"--font-sans": font.sans,
	"--font-mono": font.mono,

	"--z-dial": String(z.dial),
	"--z-dock": String(z.dock),
	"--z-appbar": String(z.appbar),
	"--z-overlay": String(z.overlay),
	"--z-modal": String(z.modal),

	"--ease-out": motion.easeOut,
	"--ease-in-out": motion.easeInOut,
	"--dur-fast": motion.durFast,
	"--dur": motion.dur,
	"--dur-slow": motion.durSlow,

	"--max-width": layout.maxWidth,
};

export default tokens;
